/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import classNames from "clsx";

import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";

import { useApi } from "@/hooks/useApi";
import { useAttendance } from "@/hooks/useAttendance";
import { useSwal } from "@/hooks/useSwal";
import { usePartogramFetus } from "@/hooks/usePartogramFetus";
import { useAttendanceIdcs } from "@/hooks/useAttendanceIdcs";
import { FetalSituation } from "@/enums/FetalSituation";
import { FetalPresentation } from "@/enums/FetalPresentation";
import { DeflectedFetalPresentationGrade } from "@/enums/DeflectedFetalPresentationGrade";
import { FetusModalStatus } from "@/enums/FetusModalStatus";
import { medicalSurgicalAct } from "@/utils/initialValues/medicalSurgicalAct";
import { filterObjectExcluding } from "@/utils/filterObject";

import { FormattedFormControl } from "@/components/FormControl/FormattedFormControl";
import { FormGroupRadio } from "@/components/Radio/FormGroupRadio";
import { FormRadio } from "@/components/Radio/FormRadio";
import { Divider } from "@/components/Divider";
import { TagHTMLOrSkeleton } from "@/components/TagHTMLOrSkeleton";
import { SkeletonWrapper } from "@/components/SkeletonWrapper";
import { SaveButton } from "@/components/Buttons/SaveButton";
import { IdcsSelector } from "@/components/IdcsSelector";
import { AddFetusModal } from "./AddFetusModal";
import { PartogramFetusCharts } from "./PartogramFetusCharts";
import { FillingHistoryModal } from "./FillingHistoryModal";

type PaginatedPartogramFetuses = GenericPagination<PartogramFetus>;

type Props = {
	remainingTime: number;
};

export function MedicalSurgicalActView({ remainingTime }: Props) {
	const { api } = useApi();
	const { attendance } = useAttendance();
	const { Toast } = useSwal();

	const { setFetusModalStatus } = usePartogramFetus();

	const [skeleton, setSkeleton] = useState(true);
	const [isPostSubmit, setIsPostSubmit] = useState(true);
	const [isDisabled, setIsDisabled] = useState(false);

	const [fetusId, setFetusId] = useState(0);
	const [fetusIndex, setFetusIndex] = useState(0);

	const completedGetMedicalSurgicalAct = useRef(false);

	const [showAddFetusModal, setShowAddFetusModal] = useState(false);
	const handleCloseAddFetusModal = () => setShowAddFetusModal(false);
	const handleShowAddFetusModal = () => setShowAddFetusModal(true);

	const [showFillingHistoryModal, setShowFillingHistoryModal] = useState(false);
	const handleCloseFillingHistoryModal = () => setShowFillingHistoryModal(false);
	const handleOpenFillingHistoryModal = () => setShowFillingHistoryModal(true);

	const expertise = attendance.surgicalAttendance?.expertise;
	const isPregnant = expertise?.pregnant;

	const formik = useFormik({
		initialValues: medicalSurgicalAct,
		async onSubmit() {
			try {
				setIsDisabled(true);

				await handleSubmit();

				Toast.fire({
					title: "Ato cirúrgico médico salvo com sucesso!",
					icon: "success",
				});
			} catch (error) {
				console.log(error);

				Toast.fire({
					title: "Erro ao salvar ato cirúrgico médico!",
					icon: "error",
				});
			} finally {
				setIsDisabled(false);
			}
		},
	});

	async function handleSubmit() {
		const pregnantFields = [
			"fetal_situation",
			"fetal_presentation",
			"flexed_fetal_presentation",
			"deflected_fetal_presentation_grade",
		];

		const filteredValues = filterObjectExcluding(formik.values, pregnantFields);

		const method = isPostSubmit ? "post" : "put";

		await api[method](
			`surgical-center/${attendance.id}/medical-surgical-act`,
			isPregnant ? formik.values : filteredValues
		);

		setIsPostSubmit(false);
	}

	async function getMedicalSurgicalAct() {
		try {
			setSkeleton(true);

			const { data } = await api.get(`surgical-center/${attendance.id}/medical-surgical-act`);

			formik.setValues(data);
			setIsPostSubmit(false);
		} catch (error) {
			console.log(error);
		} finally {
			completedGetMedicalSurgicalAct.current = true;
			setSkeleton(false);
		}
	}

	useEffect(() => {
		if (attendance.id) {
			getMedicalSurgicalAct();
			getPartogramFetuses();
		}
	}, [attendance.id]);

	useEffect(() => {
		if (!remainingTime && attendance.id && completedGetMedicalSurgicalAct.current) {
			handleSubmit();
		}
	}, [remainingTime, attendance.id, completedGetMedicalSurgicalAct.current]);

	const [currentFetusSelectedKey, setCurrentFetusSelectedKey] = useState("");
	const [isLoadingPartogramFetuses, setIsLoadingPartogramFetuses] = useState(isPregnant);
	const [partogramFetuses, setPartogramFetuses] = useState<PaginatedPartogramFetuses>(
		{} as PaginatedPartogramFetuses
	);

	async function getPartogramFetuses(next?: boolean) {
		try {
			setIsLoadingPartogramFetuses(true);

			const baseApiUrl = `/partogram-fetuses/${attendance.id}`;
			const url = next ? partogramFetuses.links?.next || baseApiUrl : baseApiUrl;

			const { data } = await api.get(url);

			const oldPartogramFetuses = partogramFetuses.results ?? [];

			if (!oldPartogramFetuses.length && data.results?.length) {
				setCurrentFetusSelectedKey(`partogram-fetus-${data.results[0].id}`);
			}

			const results = next ? [...oldPartogramFetuses, ...data.results] : data.results;
			setPartogramFetuses({ ...data, results });
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoadingPartogramFetuses(false);
		}
	}

	const [currentTabFetuses, setCurrentTabFetuses] = useState<PartogramFetus[] | null>(null);

	function setupFetusModal() {
		setFetusModalStatus(FetusModalStatus.Registering);
		setTimeout(handleShowAddFetusModal, 1);
	}

	function handleAddFetusRegistration() {
		setCurrentTabFetuses(partogramFetuses.results ?? []);
		setupFetusModal();
	}

	function handleAddOnlyFetus() {
		setCurrentTabFetuses(null);
		setupFetusModal();
	}

	const [isLoadingFillingHistory, setIsLoadingFillingHistory] = useState(false);
	const [fillingHistory, setFillingHistory] = useState<PaginatedFillingHistory>(
		{} as PaginatedFillingHistory
	);

	async function handleSearchFillingHistory(pageToUse?: number) {
		try {
			if (!fetusId) {
				return;
			}

			const page = pageToUse || 1;

			const { data } = await api.get(`partogram/fetus/${fetusId}/fillings`, {
				params: { page },
			});

			if (page === 1) {
				setFillingHistory(data);
			} else {
				const combinedResults = [...(fillingHistory.results || []), ...data.results];
				setFillingHistory({ ...data, results: combinedResults });
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoadingFillingHistory(false);
		}
	}

	useEffect(() => {
		setIsLoadingFillingHistory(true);
	}, [currentFetusSelectedKey]);

	const { attendanceIdcs, isLoadingAttendanceIdcs, actions } = useAttendanceIdcs();

	const lastAppointmentId = attendance.lastAppointment?.id;

	useEffect(() => {
		if (lastAppointmentId) {
			actions.getAllBy(lastAppointmentId);
		}
	}, [lastAppointmentId]);

	async function handleCreateAttendanceIdc(idc: ReactSelectOption) {
		const attendanceId = attendance.id;
		const idcCode = idc.value;

		if (!attendanceId || !idcCode) {
			return;
		}

		actions.create(attendanceId, idc.value);
	}

	return (
		<>
			<AddFetusModal
				tabFetuses={currentTabFetuses}
				updateTabFetusesList={getPartogramFetuses}
				show={showAddFetusModal}
				onHide={handleCloseAddFetusModal}
				fillingHistory={{
					fetch: handleSearchFillingHistory,
					setIsLoading: setIsLoadingFillingHistory,
					openModal: handleOpenFillingHistoryModal,
				}}
			/>

			<Form onSubmit={formik.handleSubmit}>
				<div className="px-5 mb-10">
					<IdcsSelector
						attendanceIdcs={attendanceIdcs}
						skeleton={isLoadingAttendanceIdcs}
						onChangeIdcType={actions.changeType}
						onRemoveIdc={actions.remove}
						onChange={(newValue) =>
							handleCreateAttendanceIdc(newValue as ReactSelectOption)
						}
					/>
				</div>

				<Divider />

				<div className="px-5 mb-10 mt-5">
					<Row className="gap-3">
						<Col lg={8} md={12}>
							<FormattedFormControl
								as="textarea"
								id="pre-operative-diagnosis"
								label="Diagnóstico Pré-operatório"
								placeholder="Descreva"
								{...formik.getFieldProps("pre_operative_diagnosis")}
								name="pre_operative_diagnosis"
								skeleton={skeleton}
								skeletonHeight={60}
							/>
						</Col>

						<Col lg={8} md={12}>
							<FormattedFormControl
								as="textarea"
								id="post-operative-diagnosis"
								label="Diagnóstico Pós-operatório"
								placeholder="Descreva"
								{...formik.getFieldProps("post_operative_diagnosis")}
								name="post_operative_diagnosis"
								skeleton={skeleton}
								skeletonHeight={60}
							/>
						</Col>

						<Col lg={8} md={12}>
							<FormattedFormControl
								as="textarea"
								id="description"
								label="Descrição"
								placeholder="Descreva"
								{...formik.getFieldProps("description")}
								name="description"
								skeleton={skeleton}
								skeletonHeight={60}
							/>
						</Col>
					</Row>
				</div>

				<Divider />

				<div className="px-5 mt-5">
					<Row>
						<Col lg={4} md={12}>
							<FormGroupRadio
								name="accident_during_surgery"
								label="Acidente durante ato cirúrgico"
								requestedValue={formik.values.accident_during_surgery}
								onChange={formik.handleChange}
								skeleton={skeleton}
								inline
							>
								<FormRadio label="Sim" value="1" id="accident-during-surgery-yes" />
								<FormRadio label="Não" value="0" id="accident-during-surgery-no" />
							</FormGroupRadio>
						</Col>

						<Col lg={4} md={12}>
							<FormGroupRadio
								name="blood_transfusion"
								label="Transfusão de hemotransfusão"
								requestedValue={formik.values.blood_transfusion}
								onChange={formik.handleChange}
								skeleton={skeleton}
								inline
							>
								<FormRadio label="Sim" value="1" id="blood-transfusion-yes" />
								<FormRadio label="Não" value="0" id="blood-transfusion-no" />
							</FormGroupRadio>
						</Col>

						<Col lg={4} md={12}>
							<FormGroupRadio
								name="histopathological_examination_material_sent"
								label="Material Enviado para Exame Histopatológico"
								requestedValue={
									formik.values.histopathological_examination_material_sent
								}
								onChange={formik.handleChange}
								skeleton={skeleton}
								inline
							>
								<FormRadio
									label="Sim"
									value="1"
									id="histopathological-examination-material-sent-yes"
								/>
								<FormRadio
									label="Não"
									value="0"
									id="histopathological-examination-material-sent-no"
								/>
							</FormGroupRadio>
						</Col>

						<Col lg={8} md={12} className="mt-10">
							<FormattedFormControl
								as="textarea"
								id="observations"
								label="Observações"
								placeholder="Descreva"
								{...formik.getFieldProps("observations")}
								name="observations"
								skeleton={skeleton}
								skeletonHeight={60}
							/>
						</Col>
					</Row>
				</div>

				{isPregnant && (
					<>
						<Divider dividerClassName="mt-10" />

						<div className="px-5 mt-5 mb-10">
							<Col className="my-3">
								<TagHTMLOrSkeleton
									text="Partograma"
									tag="p"
									className="font-weight-bold h6"
									skeleton={skeleton}
									skeletonWidth="20%"
								/>
							</Col>

							<Row className="gap-4">
								<Col lg={2} md={12}>
									<FormGroupRadio
										name="fetal_situation"
										label="Situação Fetal"
										requestedValue={formik.values.fetal_situation}
										onChange={formik.handleChange}
										skeleton={skeleton}
										skeletonHeight={50}
									>
										<FormRadio
											label="Longitudinal"
											value={FetalSituation.Longitudinal}
											id="fetal-situation-longitudinal"
										/>
										<FormRadio
											label="Transversal"
											value={FetalSituation.Transversal}
											id="fetal-situation-transversal"
										/>
									</FormGroupRadio>
								</Col>

								<Col lg={2} md={12}>
									<FormGroupRadio
										name="fetal_presentation"
										label="Apresentação Fetal"
										requestedValue={formik.values.fetal_presentation}
										onChange={formik.handleChange}
										skeleton={skeleton}
										skeletonHeight={60}
									>
										<FormRadio
											label="Cefálica"
											value={FetalPresentation.Cephalic}
											id="fetal-presentation-cephalic"
										/>
										<FormRadio
											label="Pélvica"
											value={FetalPresentation.Pelvic}
											id="fetal-presentation-pelvic"
										/>
										<FormRadio
											label="Córmica"
											value={FetalPresentation.Cormic}
											id="fetal-presentation-cormic"
										/>
									</FormGroupRadio>
								</Col>

								<Col lg={2} md={12}>
									<FormGroupRadio
										name="flexed_fetal_presentation"
										label="Fletida"
										requestedValue={formik.values.flexed_fetal_presentation}
										onChange={formik.handleChange}
										skeleton={skeleton}
										skeletonHeight={35}
										inline
									>
										<FormRadio
											label="Sim"
											value="1"
											id="flexed-fetal-presentation-yes"
										/>
										<FormRadio
											label="Não"
											value="0"
											id="flexed-fetal-presentation-no"
										/>
									</FormGroupRadio>
								</Col>

								<Col lg={2} md={12}>
									<FormGroupRadio
										name="deflected_fetal_presentation_grade"
										label="Defletida"
										requestedValue={
											formik.values.deflected_fetal_presentation_grade
										}
										onChange={formik.handleChange}
										skeleton={skeleton}
										skeletonHeight={35}
										inline
									>
										<FormRadio
											label="I"
											value={DeflectedFetalPresentationGrade.FirstGrade}
											id="deflected-fetal-presentation-first-grade"
										/>
										<FormRadio
											label="II"
											value={DeflectedFetalPresentationGrade.SecondGrade}
											id="deflected-fetal-presentation-second-grade"
										/>
										<FormRadio
											label="III"
											value={DeflectedFetalPresentationGrade.ThirdGrade}
											id="deflected-fetal-presentation-third-grade"
										/>
									</FormGroupRadio>
								</Col>
							</Row>
						</div>

						<Divider />

						<div className="px-5 mt-5 mb-10">
							<div className="d-flex flex-stack">
								<div className="d-flex flex-grow-1 my-3">
									<TagHTMLOrSkeleton
										text="Monitoração de Fetos"
										tag="p"
										className="font-weight-bold h6"
										skeleton={skeleton}
										skeletonWidth="40%"
										containerClassName="flex-grow-1"
									/>
								</div>

								<div className="d-flex gap-6">
									{skeleton ? (
										<SkeletonWrapper
											count={2}
											inline
											height={40}
											width={140}
											containerClassName="d-flex gap-4"
										/>
									) : (
										<>
											<Button
												type="button"
												onClick={handleAddFetusRegistration}
											>
												Adicionar registro
											</Button>
											<Button type="button" onClick={handleAddOnlyFetus}>
												Adicionar feto
											</Button>
										</>
									)}
								</div>
							</div>

							{!isLoadingPartogramFetuses && partogramFetuses.links?.next && (
								<div className="mb-4">
									Exibindo apenas os primeiros{" "}
									{partogramFetuses.meta?.per_page *
										partogramFetuses.meta?.current_page}{" "}
									fetos.{" "}
									<span
										className="text-primary"
										role="button"
										onClick={() => getPartogramFetuses(true)}
									>
										Mostrar mais
									</span>
								</div>
							)}

							<div className="mt-10">
								{isLoadingPartogramFetuses ? (
									<SkeletonWrapper width="100%" height={30} />
								) : (
									<Tabs
										className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder"
										activeKey={currentFetusSelectedKey}
										onSelect={(selectedKey) =>
											setCurrentFetusSelectedKey(selectedKey ?? "")
										}
									>
										{partogramFetuses.results?.map((fetus, index) => {
											const eventKey = `partogram-fetus-${fetus.id}`;
											const isActive = eventKey === currentFetusSelectedKey;
											const displayIndex = index + 1;

											return (
												<Tab
													key={eventKey}
													title={`Feto ${displayIndex}`}
													eventKey={eventKey}
													className="mt-10"
													tabClassName={classNames(
														isActive && "text-active-primary"
													)}
												>
													<div className="mb-8">
														<Button
															variant="secondary"
															type="button"
															onClick={() => {
																setFetusId(fetus.id);
																setFetusIndex(displayIndex);
																handleOpenFillingHistoryModal();
															}}
														>
															Histórico de Preenchimento
														</Button>
													</div>

													<PartogramFetusCharts
														fetusId={fetus.id}
														isActive={isActive}
													/>
												</Tab>
											);
										})}
									</Tabs>
								)}
							</div>
						</div>
					</>
				)}

				<FillingHistoryModal
					show={showFillingHistoryModal}
					onHide={handleCloseFillingHistoryModal}
					openAddFetusModal={handleShowAddFetusModal}
					fetusId={fetusId}
					fetusIndex={fetusIndex}
					fillingHistory={{
						data: fillingHistory,
						fetch: handleSearchFillingHistory,
						isLoading: isLoadingFillingHistory,
						setIsLoading: setIsLoadingFillingHistory,
					}}
				/>

				<div className="d-flex justify-content-end p-5">
					<SaveButton type="submit" disabled={isDisabled} skeleton={skeleton} />
				</div>
			</Form>
		</>
	);
}
